import {
  AIBadgeScore,
  Card,
  CollapsibleText,
  DateFormat,
  Empty,
  Group,
  Image,
  Recording,
  Skeleton,
  Tag,
  Text,
  Title,
} from "components/common"
import { useMemo, useRef } from "react"
import { t } from "i18next"
import theme from "utils/themeVars"
import { getEntityCardSectionsConfig } from "../entityCardConfig"
import { IconText, IconTextDate, Separator, StatusTag, TitleDescription } from "components/custom"
import { callStatuses } from "constants/list"
import { joinBytItems } from "utils"
import CallDrawer from "features/callRecording/components/callDrawer/callDrawer"
import { useGetCallDetailQuery } from "features/clients/detail/api"
import getPrivilege from "utils/privileges/privileges"
import { ReviewDetailCardSkeleton } from "../utils"

const renderFieldValue = (field, value, callDrawerRef) => {
  if (value === null || value === "" || value === "—") return "-"
  switch (field.type) {
    case "date":
      return <DateFormat date={value} showTime color={theme["gray900"]} />

    case "attachment":
      return Array.isArray(value) && value.length ? (
        <Image
          src={value[0].path}
          alt={value[0].name}
          width={80}
          height={80}
          style={{ objectFit: "cover", borderRadius: 4 }}
        />
      ) : null

    case "status":
      return <StatusTag data={callStatuses()[value]} />

    case "aiScore":
      return <AIBadgeScore totalScore={value} handleDrawer={() => callDrawerRef?.current?.toggleDrawer()} />

    case "iconText":
      return (
        <IconText
          iconProps={{
            color: value?.color,
            size: value?.size,
            hasBackground: value?.hasBackground,
            tooltipText: value?.tooltipText,
          }}
          title={value?.title || "-"}
          icon={value?.icon}
        />
      )

    case "iconTextDate":
      return (
        <IconTextDate
          message={value?.message}
          iconProps={{ color: value?.color, hasBackground: true }}
          icon={value?.icon}
          title={value?.title}
          date={value?.date}
        />
      )

    case "titleDescription":
      return <TitleDescription title={value?.title} description={value?.description} />

    case "recording":
      return <Recording className="w-100" recordingSrc={value} />

    case "tag":
      return (
        <Tag className="fs12" color={value?.color}>
          {value?.title}
        </Tag>
      )

    case "list":
      return (
        <span style={{ display: "inline-flex", alignItems: "center", gap: 4 }}>
          {joinBytItems(value, <Separator />)}
        </span>
      )

    case "textLong":
      return <CollapsibleText collapsibleLength={60} text={value} />

    default:
      return <Text className="fs-12">{value}</Text>
  }
}

export default function ReviewDetailCard({ selectedEntity, data, client, isPreselectEntityLoading }) {
  const sectionsConfig = useMemo(() => getEntityCardSectionsConfig(), [])
  const section = selectedEntity ? sectionsConfig[selectedEntity] : null
  const callDrawerRef = useRef()

  if (isPreselectEntityLoading) {
    return <ReviewDetailCardSkeleton />
  }

  if (!selectedEntity || (selectedEntity !== "client" && (!data || !section))) {
    return (
      <Card
        bodyStyle={{
          padding: 24,
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: 200,
          backgroundColor: "#F4FBF6",
          borderRadius: 8,
        }}
        Shadow={false}
        bordered={false}
        style={{ marginTop: 20 }}
      >
        <Empty description={t("Details will show up once an entity is selected")} />
      </Card>
    )
  }

  const entityData = selectedEntity === "client" ? client : data

  return (
    <>
      <Card
        Shadow={false}
        bordered={false}
        bodyStyle={{ padding: 16 }}
        style={{ marginTop: 16, backgroundColor: "#F4FBF6", borderRadius: 8 }}
      >
        <Group gap="12px" template="1fr">
          {section?.sectionTitle && <Title level={5}>{section.sectionTitle}</Title>}

          {section?.fields?.map(({ key, label, getValue, type }) => {
            const value = getValue?.(entityData)
            if (value === null || (type === "attachment" && !Array.isArray(value))) return null
            return (
              <div key={key} className="mbe-4">
                {label && (
                  <Text className="fs-12" block color={theme["gray900"]}>
                    {label}
                  </Text>
                )}
                {renderFieldValue({ type }, value, callDrawerRef)}
              </div>
            )
          })}
        </Group>
      </Card>
      <CallDrawer
        selectedCall={selectedEntity === "call" && data}
        useQuery={useGetCallDetailQuery}
        onBack={toggleCallDrawer => {
          toggleCallDrawer()
        }}
        ref={callDrawerRef}
      />
    </>
  )
}
